import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL, CountryOptions, UserRoles } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, GraduationCap, Search, Loader2, Paperclip, X, FileText, Image, User, Download } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const Instructors = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  usePageTitle('Instructors');
  const [instructors, setInstructors] = useState([]);
  const [trainingCenters, setTrainingCenters] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const fileInputRef = useRef(null);
  
  const canExport = user?.role === UserRoles.ADMIN;
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    id_number: '',
    instructor_number: '',
    telephone: '',
    mobile: '',
    email: '',
    personal_email: '',
    email_permission: false,
    country: '',
    city: '',
    address: '',
    training_center_ids: [],
    course_ids: []
  });

  const fetchData = async () => {
    try {
      const [instructorsRes, centersRes, coursesRes] = await Promise.all([
        axios.get(`${API_URL}/instructors`),
        axios.get(`${API_URL}/training-centers`),
        axios.get(`${API_URL}/courses`)
      ]);
      setInstructors(instructorsRes.data);
      setTrainingCenters(centersRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle edit query parameter from InstructorDetail page
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && instructors.length > 0 && !loading) {
      const instructorToEdit = instructors.find(i => i.id === editId);
      if (instructorToEdit) {
        handleEdit(instructorToEdit);
        // Clear the query param after opening dialog
        setSearchParams({});
      }
    }
  }, [searchParams, instructors, loading]);

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      id_number: '',
      instructor_number: '',
      telephone: '',
      mobile: '',
      email: '',
      personal_email: '',
      email_permission: false,
      country: '',
      city: '',
      address: '',
      training_center_ids: [],
      course_ids: []
    });
  };

  // Export instructors to CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/instructors/export/csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'instructors_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${instructors.length} instructors to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export instructors');
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.id_number || !formData.instructor_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    
    try {
      const payload = { ...formData };
      // Convert empty strings to null for optional fields
      if (payload.email === '') payload.email = null;
      if (payload.personal_email === '') payload.personal_email = null;
      if (payload.telephone === '') payload.telephone = null;
      if (payload.mobile === '') payload.mobile = null;
      if (payload.country === '') payload.country = null;
      if (payload.city === '') payload.city = null;
      if (payload.address === '') payload.address = null;
      
      if (editingInstructor) {
        await axios.put(`${API_URL}/instructors/${editingInstructor.id}`, payload);
        toast.success('Instructor updated successfully');
      } else {
        await axios.post(`${API_URL}/instructors`, payload);
        toast.success('Instructor created successfully', {
          description: formData.email ? 'An account has been created for this instructor' : undefined
        });
      }
      
      setDialogOpen(false);
      setEditingInstructor(null);
      resetForm();
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save instructor';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (instructor) => {
    setEditingInstructor(instructor);
    setFormData({
      first_name: instructor.first_name,
      last_name: instructor.last_name,
      id_number: instructor.id_number,
      instructor_number: instructor.instructor_number,
      telephone: instructor.telephone || '',
      mobile: instructor.mobile || '',
      email: instructor.email || '',
      personal_email: instructor.personal_email || '',
      email_permission: instructor.email_permission || false,
      country: instructor.country || '',
      city: instructor.city || '',
      address: instructor.address || '',
      training_center_ids: instructor.training_center_ids || [],
      course_ids: instructor.course_ids || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (instructor) => {
    setDeleteConfirm(instructor);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const instructor = deleteConfirm;
    setDeleteConfirm(null);
    
    try {
      await axios.delete(`${API_URL}/instructors/${instructor.id}`);
      toast.success('Instructor deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete instructor');
    }
  };

  const handleFileUpload = async (instructorId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API_URL}/instructors/${instructorId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    }
  };

  const handleDeleteAttachment = async (instructorId, attachmentId) => {
    try {
      await axios.delete(`${API_URL}/instructors/${instructorId}/attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const getTrainingCenterNames = (ids) => {
    if (!ids || ids.length === 0) return '-';
    return ids.map(id => {
      const center = trainingCenters.find(c => c.id === id);
      return center?.name || id;
    }).join(', ');
  };

  const getCourseNames = (ids) => {
    if (!ids || ids.length === 0) return '-';
    return ids.map(id => {
      const course = courses.find(c => c.id === id);
      return course?.name || id;
    }).join(', ');
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <Image className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const filteredInstructors = instructors.filter(instructor =>
    `${instructor.first_name} ${instructor.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    instructor.instructor_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (instructor.email && instructor.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="instructors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Instructors
          </h1>
          <p className="text-slate-500 mt-1">
            Manage instructors and their assignments
          </p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <Button 
              variant="outline"
              onClick={handleExportCSV}
              disabled={exporting || instructors.length === 0}
              data-testid="export-instructors-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
          )}
          <Button 
            className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
            onClick={() => {
              setEditingInstructor(null);
              resetForm();
              setDialogOpen(true);
            }}
            data-testid="add-instructor-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Instructor
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, instructor number, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="instructor-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Instructors Table */}
      <Card>
        <CardContent className="p-0">
          {filteredInstructors.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No instructors found</h3>
              <p className="text-slate-500 mt-1">
                {searchTerm ? 'Try a different search term' : 'Get started by adding your first instructor'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Instructor #</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Courses</TableHead>
                  <TableHead className="font-semibold">Training Centers</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInstructors.map((instructor) => (
                  <TableRow key={instructor.id} className="hover:bg-slate-50/50" data-testid={`instructor-row-${instructor.id}`}>
                    <TableCell 
                      className="font-medium cursor-pointer hover:text-[#2C9DE1] hover:underline"
                      onClick={() => navigate(`/instructors/${instructor.id}`)}
                    >
                      {instructor.first_name} {instructor.last_name}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                        {instructor.instructor_number}
                      </span>
                    </TableCell>
                    <TableCell>{instructor.email || '-'}</TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600 truncate max-w-[200px] block">
                        {getCourseNames(instructor.course_ids)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600 truncate max-w-[200px] block">
                        {getTrainingCenterNames(instructor.training_center_ids)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`instructor-actions-${instructor.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/instructors/${instructor.id}`)}>
                            <User className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(instructor)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*,.pdf,.doc,.docx';
                              input.onchange = (e) => {
                                const file = e.target.files[0];
                                if (file) handleFileUpload(instructor.id, file);
                              };
                              input.click();
                            }}
                          >
                            <Paperclip className="w-4 h-4 mr-2" />
                            Upload File
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(instructor)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingInstructor ? 'Edit Instructor' : 'Add New Instructor'}
            </DialogTitle>
            <DialogDescription>
              {editingInstructor ? 'Update instructor details' : 'Add a new instructor to the system'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="form-section-header">Personal Information</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="John"
                    data-testid="instructor-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Doe"
                    data-testid="instructor-lastname-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="id_number">ID Number *</Label>
                  <Input
                    id="id_number"
                    value={formData.id_number}
                    onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                    placeholder="CY12345678"
                    data-testid="instructor-idnumber-input"
                  />
                  <p className="text-xs text-slate-500">
                    Used as initial password for instructor account
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instructor_number">Instructor Number *</Label>
                  <Input
                    id="instructor_number"
                    value={formData.instructor_number}
                    onChange={(e) => setFormData({ ...formData, instructor_number: e.target.value })}
                    placeholder="INS001"
                    data-testid="instructor-number-input"
                  />
                  <p className="text-xs text-slate-500">
                    Unique identifier for this instructor
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="form-section-header">Contact Information</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telephone">Telephone</Label>
                  <Input
                    id="telephone"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    placeholder="+357 22 123456"
                    data-testid="instructor-telephone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile</Label>
                  <Input
                    id="mobile"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    placeholder="+357 99 123456"
                    data-testid="instructor-mobile-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Work Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john.doe@company.com"
                    data-testid="instructor-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="personal_email">Personal Email</Label>
                  <Input
                    id="personal_email"
                    type="email"
                    value={formData.personal_email}
                    onChange={(e) => setFormData({ ...formData, personal_email: e.target.value })}
                    placeholder="john.doe@gmail.com"
                    data-testid="instructor-personalemail-input"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="email_permission"
                  checked={formData.email_permission}
                  onCheckedChange={(checked) => setFormData({ ...formData, email_permission: checked })}
                  data-testid="instructor-emailpermission-checkbox"
                />
                <Label htmlFor="email_permission" className="text-sm font-normal">
                  Permission to send promotional emails
                </Label>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <h3 className="form-section-header">Address</h3>
              
              <div className="space-y-2">
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main Street"
                  data-testid="instructor-address-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Nicosia"
                    data-testid="instructor-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={formData.country || "none"}
                    onValueChange={(value) => setFormData({ ...formData, country: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="instructor-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select country</SelectItem>
                      {CountryOptions.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Training Centers Assignment */}
            <div className="space-y-4">
              <h3 className="form-section-header">Training Center Assignment</h3>
              
              <div className="space-y-2">
                <Label>Assigned Training Centers ({formData.training_center_ids.length} selected)</Label>
                <Select
                  value="placeholder"
                  onValueChange={(value) => {
                    if (value !== "placeholder" && !formData.training_center_ids.includes(value)) {
                      setFormData({ ...formData, training_center_ids: [...formData.training_center_ids, value] });
                    }
                  }}
                >
                  <SelectTrigger data-testid="instructor-centers-select">
                    <SelectValue placeholder="Add training center..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder" disabled className="hidden">Add training center...</SelectItem>
                    {trainingCenters.filter(c => !formData.training_center_ids.includes(c.id)).map((center) => (
                      <SelectItem key={center.id} value={center.id}>
                        {center.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {formData.training_center_ids.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.training_center_ids.map(id => {
                      const center = trainingCenters.find(c => c.id === id);
                      return (
                        <Badge 
                          key={id} 
                          variant="secondary"
                          className="cursor-pointer hover:bg-red-100"
                          onClick={() => setFormData({ 
                            ...formData, 
                            training_center_ids: formData.training_center_ids.filter(cid => cid !== id) 
                          })}
                        >
                          {center ? center.name : id} ×
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Courses Assignment */}
            <div className="space-y-4">
              <h3 className="form-section-header">Courses This Instructor Can Teach</h3>
              
              <div className="space-y-2">
                <Label>Assigned Courses ({formData.course_ids.length} selected)</Label>
                <Select
                  value="placeholder"
                  onValueChange={(value) => {
                    if (value !== "placeholder" && !formData.course_ids.includes(value)) {
                      setFormData({ ...formData, course_ids: [...formData.course_ids, value] });
                    }
                  }}
                >
                  <SelectTrigger data-testid="instructor-courses-select">
                    <SelectValue placeholder="Add course..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder" disabled className="hidden">Add course...</SelectItem>
                    {courses.filter(c => !formData.course_ids.includes(c.id)).map((course) => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {formData.course_ids.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.course_ids.map(id => {
                      const course = courses.find(c => c.id === id);
                      return (
                        <Badge 
                          key={id} 
                          variant="secondary"
                          className="cursor-pointer hover:bg-red-100"
                          onClick={() => setFormData({ 
                            ...formData, 
                            course_ids: formData.course_ids.filter(cid => cid !== id) 
                          })}
                        >
                          {course ? course.name : id} ×
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Attachments (only when editing) */}
            {editingInstructor && (
              <div className="space-y-4">
                <h3 className="form-section-header">Attachments</h3>
                
                {/* Existing attachments */}
                {editingInstructor.attachments?.length > 0 && (
                  <div className="space-y-2">
                    {editingInstructor.attachments.map((att) => (
                      <div key={att.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          {getFileIcon(att.content_type)}
                          <a 
                            href={`${API_URL}/files/${att.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#2C9DE1] hover:underline"
                          >
                            {att.filename}
                          </a>
                          <Badge variant="secondary" className="text-xs">
                            {(att.size / 1024).toFixed(1)} KB
                          </Badge>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAttachment(editingInstructor.id, att.id)}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload new file */}
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleFileUpload(editingInstructor.id, e.target.files[0]);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip className="w-4 h-4 mr-2" />
                    Add Attachment
                  </Button>
                  <span className="text-xs text-slate-500">
                    PDF, Word, or images (max 10MB)
                  </span>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={saving}
                data-testid="instructor-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingInstructor ? 'Update Instructor' : 'Create Instructor'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Delete Instructor
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteConfirm.first_name} {deleteConfirm.last_name}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                variant="outline"
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white border-0"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Instructors;
